import {Component, DestroyRef, inject} from '@angular/core';
import {DxChartModule, DxSelectBoxModule} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    GetPostProductionMdtScoreQuery,
    GetPostProductionMdtTrendingQuery,
    MyScoreTrendingGrouping,
    OrdersClient,
} from '@services';
import {DoughnutChartTableComponent, DoughnutChartTableSlice} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {OutboundMyScoreFilterBarComponent, OutboundMyScoreFilters} from '../outbound-my-score-filter-bar/outbound-my-score-filter-bar.component';

type TrendingPoint = { period: Date; onTime: number; late: number };

export function postProductionMdtPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        ['In Production']: getColor('--bs-secondary'),
        ['Picking']: getColor('--bs-primary'),
        ['Ready To Load']: getColor('--bs-warning'),
        ['Loading']: getColor('--bs-info'),
        ['Loaded On Time']: getColor('--bs-success'),
        ['Loaded Late']: getColor('--bs-danger'),
    };
}

export function postProductionCenterText(from: Date | undefined, to: Date | undefined): string {
    return from && to && from.toDateString() === to.toDateString() ? 'Loads For Today' : 'Loads';
}

@Component({
    selector: 'app-post-production-mdt',
    imports: [DoughnutChartTableComponent, OutboundMyScoreFilterBarComponent, DxChartModule, DxSelectBoxModule],
    templateUrl: './post-production-mdt.component.html',
})
export class PostProductionMdtComponent {
    private ordersClient = inject(OrdersClient);
    private destroyRef = inject(DestroyRef);

    protected readonly InProduction = 'In Production';
    protected readonly Picking = 'Picking';
    protected readonly ReadyToLoad = 'Ready To Load';
    protected readonly Loading = 'Loading';
    protected readonly LoadedOnTime = 'Loaded On Time';
    protected readonly LoadedLate = 'Loaded Late';
    private readonly buckets = [this.InProduction, this.Picking, this.ReadyToLoad, this.Loading, this.LoadedOnTime, this.LoadedLate];

    protected palette: Record<string, string> = {};
    protected onTimeColor = '';
    protected lateColor = '';

    protected grouping = MyScoreTrendingGrouping.Day;
    protected groupingOptions = [
        {value: MyScoreTrendingGrouping.Day, text: 'Day'},
        {value: MyScoreTrendingGrouping.Hour, text: 'Hour'},
    ];

    protected slices: DoughnutChartTableSlice[] = [];
    protected totalLoads = 0;
    protected trendingData: TrendingPoint[] = [];

    private filters?: OutboundMyScoreFilters;

    constructor() {
        this.palette = postProductionMdtPalette();
        this.onTimeColor = this.palette[this.LoadedOnTime];
        this.lateColor = this.palette[this.LoadedLate];
    }

    protected get centerText(): string {
        return postProductionCenterText(this.filters?.from, this.filters?.to);
    }

    protected onFiltersChanged(filters: OutboundMyScoreFilters): void {
        this.filters = filters;
        this.loadScore();
        this.loadTrending();
    }

    protected onGroupingChanged(): void {
        this.loadTrending();
    }

    protected customizeTrendingTooltip = (info: { seriesName: string; value: number }) => ({
        text: `${info.seriesName}: ${info.value}%`,
    });

    protected percentAxisLabel = (info: { value: number }) => `${info.value}%`;

    private loadScore(): void {
        if (!this.filters) return;

        this.ordersClient
            .getPostProductionMdtScore(new GetPostProductionMdtScoreQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                const totals = dto.totals ?? {};
                this.slices = this.buckets.map(key => ({argument: key, value: totals[key] ?? 0}));
                this.totalLoads = dto.totalLoads ?? 0;
            });
    }

    private loadTrending(): void {
        if (!this.filters) return;

        this.ordersClient
            .getPostProductionMdtTrending(new GetPostProductionMdtTrendingQuery({...this.filters, grouping: this.grouping}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dto => {
                this.trendingData = (dto.points ?? []).map(p => ({
                    period: p.period!,
                    onTime: p.loadedOnTimePercent ?? 0,
                    late: p.loadedLatePercent ?? 0,
                }));
            });
    }
}
